import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { DialogHeader, DialogFooter } from "@/components/ui/dialog";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Store, Warehouse, Globe, Plus, Badge, HomeIcon, Edit, CheckCircle2 } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useForm, Form } from "react-hook-form";
import { Switch } from "@/components/ui/switch";
import { z } from "zod";

// Define store types
type StoreType = {
  id: string;
  name: string;
  type: "retail" | "warehouse" | "online";
  is_active: boolean;
  is_default: boolean;
  location?: string;
  created_at: string;
  updated_at: string;
};

type StoreTypeEnum = "retail" | "warehouse" | "online";

// Form schema for store
const storeSchema = z.object({
  name: z.string().min(1, "Store name is required"),
  type: z.enum(["retail", "warehouse", "online"] as const),
  is_active: z.boolean().default(true),
  is_default: z.boolean().default(false),
  location: z.string().optional()
});

type StoreFormValues = z.infer<typeof storeSchema>;

export default function StoresPage() {
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingStore, setEditingStore] = useState<StoreType | null>(null);
  const [stores, setStores] = useState<StoreType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Create form
  const form = useForm<StoreFormValues>({
    resolver: zodResolver(storeSchema),
    defaultValues: {
      name: "",
      type: "retail",
      is_active: true,
      is_default: false,
      location: ""
    }
  });

  // Load mock stores data
  useEffect(() => {
    const mockStores: StoreType[] = [
      {
        id: "1",
        name: "Main Store",
        type: "retail",
        is_active: true,
        is_default: true,
        location: "123 Main St, Downtown",
        created_at: "2024-01-01T00:00:00Z",
        updated_at: "2024-01-01T00:00:00Z"
      },
      {
        id: "2",
        name: "Central Warehouse",
        type: "warehouse",
        is_active: true,
        is_default: false,
        location: "456 Industrial Ave",
        created_at: "2024-01-01T00:00:00Z",
        updated_at: "2024-01-01T00:00:00Z"
      },
      {
        id: "3",
        name: "Online Shop",
        type: "online",
        is_active: true,
        is_default: false,
        location: "www.onlineshop.com",
        created_at: "2024-01-01T00:00:00Z",
        updated_at: "2024-01-01T00:00:00Z"
      }
    ];

    // Simulate loading delay
    setTimeout(() => {
      setStores(mockStores);
      setIsLoading(false);
    }, 500);
  }, []);

  const handleCreateStore = async (values: StoreFormValues) => {
    try {
      if (editingStore) {
        // Update existing store
        const updatedStore = { ...editingStore, ...values, updated_at: new Date().toISOString() };
        setStores(prev => prev.map(store =>
          store.id === editingStore.id ? updatedStore : store
        ));

        toast({
          title: "Store updated",
          description: `${values.name} has been updated successfully`,
        });
      } else {
        // Create new store
        const newStore: StoreType = {
          id: Date.now().toString(),
          ...values,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        setStores(prev => [...prev, newStore]);

        toast({
          title: "Store created",
          description: `${values.name} has been created successfully`,
        });
      }

      // Reset form and close dialog
      form.reset();
      setEditingStore(null);
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error saving store:", error);
      toast({
        title: "Failed to save store",
        description: "There was an error while saving the store. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleSetDefault = async (store: StoreType) => {
    if (store.is_default) return; // Already default

    try {
      // Update all stores to set is_default to false
      setStores(prev => prev.map(s => ({
        ...s,
        is_default: s.id === store.id ? true : false,
        updated_at: new Date().toISOString()
      })));

      toast({
        title: "Default store updated",
        description: `${store.name} is now the default store`,
      });
    } catch (error) {
      console.error("Error setting default store:", error);
      toast({
        title: "Failed to update default store",
        description: "There was an error while updating. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleEditStore = (store: StoreType) => {
    setEditingStore(store);

    form.reset({
      name: store.name,
      type: store.type as "retail" | "warehouse" | "online",
      is_active: store.is_active || false,
      is_default: store.is_default || false,
      location: store.location || ""
    });

    setIsDialogOpen(true);
  };

  const handleAddStore = () => {
    form.reset({
      name: "",
      type: "retail",
      is_active: true,
      is_default: false,
      location: ""
    });
    setEditingStore(null);
    setIsDialogOpen(true);
  };

  // Separate stores by type for better organization
  const retailStores = stores.filter((store: StoreType) => store.type === 'retail');
  const warehouseStores = stores.filter((store: StoreType) => store.type === 'warehouse');
  const onlineStores = stores.filter((store: StoreType) => store.type === 'online');

  // Return store icon based on type
  const getStoreIcon = (type: "retail" | "warehouse" | "online") => {
    switch (type) {
      case 'retail':
        return <Store className="h-5 w-5 text-blue-600" />;
      case 'warehouse':
        return <Warehouse className="h-5 w-5 text-blue-600" />;
      case 'online':
        return <Globe className="h-5 w-5 text-blue-600" />;
      default:
        return <Store className="h-5 w-5 text-blue-600" />;
    }
  };

  return (
    <div className="container mx-auto py-6 max-w-6xl">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-blue-700">Stores & Locations</h1>
        <Button
          className="gap-2 bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900"
          onClick={handleAddStore}
        >
          <Plus size={16} />
          <span>Add Store</span>
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center my-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Retail Stores */}
          <div>
            <h2 className="text-2xl font-semibold text-blue-800 mb-4 flex items-center gap-2">
              <Store className="h-6 w-6" />
              Retail Stores
            </h2>
            {retailStores.length === 0 ? (
              <p className="text-gray-500">No retail stores. Add one to get started.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {retailStores.map((store: StoreType) => (
                  <StoreCard
                    key={store.id}
                    store={store}
                    onEdit={() => handleEditStore(store)}
                    onSetDefault={() => handleSetDefault(store)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Warehouses */}
          <div>
            <h2 className="text-2xl font-semibold text-blue-800 mb-4 flex items-center gap-2">
              <Warehouse className="h-6 w-6" />
              Warehouses
            </h2>
            {warehouseStores.length === 0 ? (
              <p className="text-gray-500">No warehouses. Add one to get started.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {warehouseStores.map((store: StoreType) => (
                  <StoreCard
                    key={store.id}
                    store={store}
                    onEdit={() => handleEditStore(store)}
                    onSetDefault={() => handleSetDefault(store)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Online Stores */}
          <div>
            <h2 className="text-2xl font-semibold text-blue-800 mb-4 flex items-center gap-2">
              <Globe className="h-6 w-6" />
              Online Stores
            </h2>
            {onlineStores.length === 0 ? (
              <p className="text-gray-500">No online stores. Add one to get started.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {onlineStores.map((store: StoreType) => (
                  <StoreCard
                    key={store.id}
                    store={store}
                    onEdit={() => handleEditStore(store)}
                    onSetDefault={() => handleSetDefault(store)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create/Edit Store Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingStore ? `Edit ${editingStore.name}` : 'Add New Store'}
            </DialogTitle>
            <DialogDescription>
              {editingStore
                ? 'Update the details for this store location'
                : 'Create a new store location for inventory management'}
            </DialogDescription>
          </DialogHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleCreateStore)} className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Store Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter store name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Store Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select store type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="retail">Retail Store</SelectItem>
                        <SelectItem value="warehouse">Warehouse</SelectItem>
                        <SelectItem value="online">Online Store</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location (Optional)</FormLabel>
                    <FormControl>
                      <Input placeholder="Enter location address" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-between">
                <FormField
                  control={form.control}
                  name="is_active"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between space-x-2">
                      <div className="space-y-0.5">
                        <FormLabel>Active</FormLabel>
                        <FormDescription>
                          Inactive stores won't appear in stock operations
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="is_default"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between space-x-2">
                    <div className="space-y-0.5">
                      <FormLabel>Set as Default</FormLabel>
                      <FormDescription>
                        Default store will be used for new orders
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {editingStore ? 'Update Store' : 'Create Store'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StoreCard({
  store,
  onEdit,
  onSetDefault
}: {
  store: StoreType;
  onEdit: () => void;
  onSetDefault: () => void;
}) {
  // Get store icon based on type
  const getStoreIcon = (type: StoreTypeEnum) => {
    switch (type) {
      case 'retail':
        return <Store className="h-5 w-5" />;
      case 'warehouse':
        return <Warehouse className="h-5 w-5" />;
      case 'online':
        return <Globe className="h-5 w-5" />;
      default:
        return <Store className="h-5 w-5" />;
    }
  };

  return (
    <Card className={`${store.is_default ? 'border-blue-300 bg-blue-50' : 'border-gray-200'} transition-all hover:shadow-md`}>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-100 rounded-full">
              {getStoreIcon(store.type as StoreTypeEnum)}
            </div>
            <CardTitle className="text-lg font-semibold">{store.name}</CardTitle>
          </div>
          {store.is_default && (
            <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-200">
              Default
            </Badge>
          )}
        </div>
        {store.location && (
          <CardDescription className="flex items-center gap-1 text-sm mt-1">
            <HomeIcon className="h-3.5 w-3.5 text-gray-500" />
            {store.location}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="pb-2">
        <div className="flex gap-2">
          <Badge className={`
            ${store.type === 'retail' ? 'bg-cyan-100 text-cyan-800 hover:bg-cyan-200' :
              store.type === 'warehouse' ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' :
                'bg-purple-100 text-purple-800 hover:bg-purple-200'}
          `}>
            {store.type === 'retail' ? 'Retail' :
              store.type === 'warehouse' ? 'Warehouse' :
                'Online Store'}
          </Badge>
          {!store.is_active && (
            <Badge className="border-gray-300 text-gray-600 bg-gray-100">
              Inactive
            </Badge>
          )}
        </div>
      </CardContent>
      <Separator />
      <CardFooter className="pt-4 flex justify-between">
        <Button
          variant="outline"
          size="sm"
          className="text-blue-600 border-blue-200"
          onClick={onEdit}
        >
          <Edit className="h-4 w-4 mr-1" />
          Edit
        </Button>
        {!store.is_default && (
          <Button
            variant="outline"
            size="sm"
            className="text-emerald-600 border-emerald-200"
            onClick={onSetDefault}
          >
            <CheckCircle2 className="h-4 w-4 mr-1" />
            Set Default
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}